import { useSyncExternalStore } from 'react';

/**
 * One value, held OUTSIDE React, that only the components which read it
 * re-render on.
 *
 * The little sibling of `createLiveStore`: that one keys many values by pair
 * and notifies per key; this one holds a single group-level value. Same reason
 * for existing — a socket frame must not re-render the page that happens to
 * own the subscription.
 *
 * The concrete case: `group_cost_update` arrives on the opener's broadcast
 * tick (2/s on a live group, the enricher re-emits whenever `computedAt`
 * moves). Held in `useState` inside `useEntrySocket`, every one of those
 * frames re-rendered `EntryPage` — and through it the ~4.9k-line entry table —
 * to update four numbers in one strip.
 */
export interface ValueStore<T> {
  /** `useSyncExternalStore` contract. */
  subscribe(onChange: () => void): () => void;
  getSnapshot(): T;
  /** Write. A value identical by reference notifies nobody. */
  set(next: T): void;
}

export function createValueStore<T>(initial: T): ValueStore<T> {
  let current = initial;
  const listeners = new Set<() => void>();

  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    getSnapshot() {
      // MUST return the held reference, never a fresh object: React compares
      // snapshots by identity and would re-render forever otherwise.
      return current;
    },
    set(next) {
      if (Object.is(next, current)) return;
      current = next;
      for (const l of listeners) l();
    }
  };
}

/** Subscribe a component to a `ValueStore`. Only this component re-renders. */
export function useValueStore<T>(store: ValueStore<T>): T {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}
